import {VetologyBase} from "../Vetology/VetologyBase";

export class InstinctIntegration extends VetologyBase
{
    constructor()
    {
        super('InstinctIntegration');
    }

    init()
    {
        const _self = this;
        _self.panel = document.getElementById('instinctPanel');
        if (!_self.panel) {
            return;
        }

        _self.rowVersion = _self.panel.dataset.rowVersion === ''
            ? null
            : parseInt(_self.panel.dataset.rowVersion, 10);
        _self.configured = _self.panel.dataset.configured === '1';
        _self.clinicId = parseInt(_self.panel.dataset.clinicId, 10);
        _self.testPassed = false;

        _self.fields = {
            client_id: document.getElementById('instinctClientId'),
            client_secret: document.getElementById('instinctClientSecret'),
            location_uuid: document.getElementById('instinctLocationUuid'),
            environment: document.getElementById('instinctEnvironment'),
        };
        _self.labelField = document.getElementById('instinctLabel');
        _self.testButton = document.getElementById('instinctTest');
        _self.saveButton = document.getElementById('instinctSave');
        _self.enabledToggle = document.getElementById('instinctEnabled');

        _self.initEventHandlers();
    }

    initEventHandlers()
    {
        const _self = this;

        _self.onTest = _self.onTest.bind(_self);
        _self.onSave = _self.onSave.bind(_self);
        _self.onToggle = _self.onToggle.bind(_self);
        _self.onFieldChange = _self.onFieldChange.bind(_self);

        _self.testButton.addEventListener('click', _self.onTest);
        _self.saveButton.addEventListener('click', _self.onSave);
        _self.enabledToggle.addEventListener('change', _self.onToggle);

        Object.values(_self.fields).forEach((field) => {
            field.addEventListener('input', _self.onFieldChange);
            field.addEventListener('change', _self.onFieldChange);
        });
    }

    removeEventHandlers()
    {
        const _self = this;
        if (!_self.panel) {
            return;
        }
        _self.testButton.removeEventListener('click', _self.onTest);
        _self.saveButton.removeEventListener('click', _self.onSave);
        _self.enabledToggle.removeEventListener('change', _self.onToggle);
        Object.values(_self.fields).forEach((field) => {
            field.removeEventListener('input', _self.onFieldChange);
            field.removeEventListener('change', _self.onFieldChange);
        });
    }

    /**
     * Any edit invalidates the previous test, so Save closes again until the new
     * values have been proven against Instinct.
     */
    onFieldChange()
    {
        const _self = this;
        _self.testPassed = false;
        _self.saveButton.disabled = true;
    }

    onTest()
    {
        const _self = this;
        if (!_self.validate()) {
            return;
        }
        _self.setBusy(true);
        _self.post(_self.panel.dataset.testUrl, _self.credentials())
            .then((result) => {
                _self.setBusy(false);
                if (!result.success) {
                    _self.testPassed = false;
                    _self.saveButton.disabled = true;
                    _self.showError(result.message);
                    return;
                }
                _self.testPassed = true;
                _self.saveButton.disabled = false;
                _self.showSuccess(result.message);
            });
    }

    onSave()
    {
        const _self = this;
        if (!_self.testPassed || !_self.validate()) {
            return;
        }
        _self.setBusy(true);
        _self.post(_self.panel.dataset.saveUrl, _self.credentials())
            .then((result) => {
                _self.setBusy(false);
                if (!result.success) {
                    _self.showError(result.message);
                    return;
                }
                _self.applyIntegration(result.integration);
                _self.testPassed = false;
                _self.saveButton.disabled = true;
                _self.fields.client_secret.value = '';
                _self.fields.client_secret.placeholder = '••••••••';
                _self.showSuccess(result.message);
            });
    }

    onToggle()
    {
        const _self = this;
        const desired = _self.enabledToggle.checked;

        if (desired && !_self.configured) {
            _self.enabledToggle.checked = false;
            _self.showError('Please configure and test credentials before enabling the integration');
            return;
        }

        _self.enabledToggle.disabled = true;
        _self.post(_self.panel.dataset.toggleUrl, {
            enabled: desired,
            expected_row_version: _self.rowVersion,
            clinic_id: _self.clinicId,
        }).then((result) => {
            _self.enabledToggle.disabled = false;
            if (!result.success) {
                _self.enabledToggle.checked = !desired;
                _self.showError(result.message);
                return;
            }
            _self.applyIntegration(result.integration);
            _self.showSuccess(result.message);
        });
    }

    validate()
    {
        const _self = this;
        const labels = {
            client_id: 'Username is required',
            client_secret: 'Credential is required',
            location_uuid: 'Location UUID is required',
            environment: 'Select an environment',
        };

        _self.clearMessages();
        let valid = true;
        Object.keys(_self.fields).forEach((name) => {
            const value = (_self.fields[name].value || '').trim();
            const error = _self.panel.querySelector('[data-field="' + name + '"]');
            if (value === '') {
                valid = false;
                error.textContent = labels[name];
                error.classList.remove('hidden');
                _self.fields[name].closest('.form-group').classList.add('has-error');
            }
        });
        return valid;
    }

    credentials()
    {
        const _self = this;
        return {
            client_id: _self.fields.client_id.value.trim(),
            client_secret: _self.fields.client_secret.value,
            location_uuid: _self.fields.location_uuid.value.trim(),
            environment: _self.fields.environment.value,
            credential_label: _self.labelField.value.trim(),
            expected_row_version: _self.rowVersion,
            clinic_id: _self.clinicId,
        };
    }

    applyIntegration(integration)
    {
        const _self = this;
        if (!integration) {
            return;
        }
        _self.rowVersion = typeof integration.row_version === 'number' ? integration.row_version : null;
        _self.configured = integration.status !== 'not_configured';
        _self.enabledToggle.checked = !!integration.enabled;
        _self.enabledToggle.disabled = !_self.configured;

        const hint = document.getElementById('instinctToggleHint');
        if (hint && _self.configured) {
            hint.classList.add('hidden');
        }

        const badge = document.getElementById('integrationStatus');
        if (!badge) {
            return;
        }
        const labels = {
            active: ['Active', 'label-success'],
            disconnected: ['Disconnected', 'label-warning'],
            not_configured: ['Not Configured', 'label-default'],
        };
        const entry = labels[integration.status] || labels.not_configured;
        badge.textContent = entry[0];
        badge.className = 'label ' + entry[1];
    }

    setBusy(busy)
    {
        const _self = this;
        _self.testButton.disabled = busy;
        if (busy) {
            _self.saveButton.disabled = true;
        }
    }

    clearMessages()
    {
        const _self = this;
        _self.panel.querySelectorAll('.field-error').forEach((el) => {
            el.textContent = '';
            el.classList.add('hidden');
        });
        _self.panel.querySelectorAll('.has-error').forEach((el) => {
            el.classList.remove('has-error');
        });
    }

    showError(message)
    {
        toastr.error(message);
    }

    showSuccess(message)
    {
        toastr.success(message);
    }

    post(url, payload)
    {
        const _self = this;
        const token = document.querySelector('meta[name="csrf-token"]');
        return fetch(url, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'X-CSRF-TOKEN': token ? token.getAttribute('content') : '',
            },
            body: JSON.stringify(payload),
        })
            .then((response) => response.json().catch(() => null))
            .then((body) => {
                if (body && body.errors) {
                    Object.keys(body.errors).forEach((name) => {
                        const error = _self.panel.querySelector('[data-field="' + name + '"]');
                        if (error) {
                            error.textContent = body.errors[name];
                            error.classList.remove('hidden');
                        }
                    });
                    return {success: false, message: 'Please correct the highlighted fields.'};
                }
                if (!body || (!body.success && !body.message)) {
                    return {success: false, message: 'Instinct is currently unavailable. Please try again.'};
                }
                return body;
            })
            .catch(() => ({
                success: false,
                message: 'Instinct is currently unavailable. Please try again.',
            }));
    }
}
